// Whether someone is part-way through editing a list in place, read from what the cells put on the page: an open box
// (data-inline-editor), an open panel (data-inline-panel, PANEL_PORTAL), a save still waiting for its answer
// (.inline-saving), or a dialog (a status change's resolution note, a form opened from a cell). The lists' 5-minute
// refresh waits for these: reloading the rows under an open cell closed it and lost what was typed.

export const EDITING_NOW_SELECTOR =
  '[data-inline-editor="true"], [data-inline-panel="true"], .inline-saving, .ui-dialog';

export const isEditingInPlace = (root: Pick<Document, 'querySelector'> | undefined =
  typeof document === 'undefined' ? undefined : document): boolean =>
  !!root?.querySelector(EDITING_NOW_SELECTOR);

/** How often a postponed refresh looks again. */
export const EDITING_RECHECK_MS = 1000;

/**
 * Runs `run` now, or - while a cell is being edited - as soon as editing ends. A second request while one is waiting
 * replaces it, so a refresh postponed across several timer ticks runs once. Returns a cancel for the waiting one.
 */
export const createDeferredWhileEditing = (isEditing: () => boolean = isEditingInPlace, recheckMs = EDITING_RECHECK_MS) => {
  let waiting: ReturnType<typeof setTimeout> | null = null;
  let pending: (() => void) | null = null;

  const cancel = () => {
    if (waiting) clearTimeout(waiting);
    waiting = null;
    pending = null;
  };

  const check = () => {
    waiting = null;
    if (!pending) return;
    if (isEditing()) {
      waiting = setTimeout(check, recheckMs);
      return;
    }
    const run = pending;
    pending = null;
    run();
  };

  const request = (run: () => void) => {
    pending = run;
    if (!waiting) check();
  };

  return { request, cancel };
};

const deferredByTimer = new Map<unknown, ReturnType<typeof createDeferredWhileEditing>>();

/** setInterval for a list's auto refresh: each tick is postponed while a cell is being edited. Stop it with stopRefreshTimer. */
export const startRefreshTimer = (run: () => void, everyMs: number) => {
  const deferred = createDeferredWhileEditing();
  const timer = setInterval(() => deferred.request(run), everyMs);
  deferredByTimer.set(timer, deferred);
  return timer;
};

/** Stops the timer and any refresh it is holding back, so nothing runs after the list has gone. */
export const stopRefreshTimer = (timer: ReturnType<typeof setInterval> | null | undefined) => {
  if (timer == null) return;
  clearInterval(timer);
  deferredByTimer.get(timer)?.cancel();
  deferredByTimer.delete(timer);
};
